import { createContext, useContext, useMemo } from "react";

import { useParams } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";

import { ListPageLayout } from "@neu/libui";

import { usePatientCaseProgramStatuses } from "./usePatientCaseProgramStatuses";

import type { CaseProgramStatuses } from "./usePatientCaseProgramStatuses";

const EMPTY_STATUS_MAP = new Map<number, CaseProgramStatuses>();

const CaseProgramStatusContext =
	createContext<Map<number, CaseProgramStatuses>>(EMPTY_STATUS_MAP);

const columnHelper = createColumnHelper<Record<string, unknown>>();

type StatusField = keyof CaseProgramStatuses;

function ProgramStatusCell({
	caseID,
	field,
}: {
	caseID: unknown;
	field: StatusField;
}) {
	const statusByCaseID = useContext(CaseProgramStatusContext);
	let numericCaseID = NaN;
	if (typeof caseID === "number") {
		numericCaseID = caseID;
	} else if (typeof caseID === "string" && caseID.trim() !== "") {
		numericCaseID = Number(caseID);
	}
	if (!Number.isFinite(numericCaseID)) {
		return "—";
	}
	const value = statusByCaseID.get(numericCaseID)?.[field];
	return value && value.trim() !== "" ? value : "—";
}

function statusColumn(id: string, header: string, field: StatusField) {
	return columnHelper.display({
		id,
		header,
		enableSorting: false,
		cell: ({ row }) => (
			<ProgramStatusCell caseID={row.original.id} field={field} />
		),
	});
}

const PROGRAM_STATUS_COLUMNS = [
	statusColumn("case_pap_status", "PAP Status", "pap"),
	statusColumn("case_biosimilar_status", "Biosimilar Status", "biosimilar"),
	statusColumn("case_bridge_status", "Bridge Status", "bridge"),
	statusColumn("case_cap_status", "CAP Status", "cap"),
	statusColumn("case_340b_status", "340B Status", "discount340b"),
];

/**
 * Case tab list with program status columns, without changing patient_case definition.
 * Matches default nested ListPageLayout wiring from create-app-router.
 */
export function PatientCaseList() {
	const { patientID } = useParams({ strict: false });
	const statusByCaseID = usePatientCaseProgramStatuses(
		typeof patientID === "string" ? patientID : undefined
	);
	const customColumnDefs = useMemo(() => PROGRAM_STATUS_COLUMNS, []);

	return (
		<CaseProgramStatusContext.Provider value={statusByCaseID}>
			<ListPageLayout
				entity="patient_case"
				scope="patient-case"
				customColumnDefs={customColumnDefs}
				customColumnDefsPosition="append"
			/>
		</CaseProgramStatusContext.Provider>
	);
}
